import { Loader } from '@googlemaps/js-api-loader';
import { Log } from '../../AuxillaryService/Log';
import { WidgetType } from '../types';
import {
    MapsWidgetConfiguration,
    MapsWidgetConfigurationDetails,
    MapTypeId,
    ZodMapsWidgetConfigurationDetails,
} from './types';
import { WidgetWithScheduler } from '../WidgetWithScheduler';

export class MapsWidget extends WidgetWithScheduler {
    type: WidgetType.MAPS = WidgetType.MAPS;
    declare config: MapsWidgetConfigurationDetails;

    /**
     * Hold a value of a previously resolved Google Maps promise.
     */
    static googleMapsPromise: Promise<typeof google.maps> | undefined;

    /**
     * Google maps object instance containing
     * data and state pertaining to this widget's map options.
     */
    map: google.maps.Map | null;

    constructor(widgetConfiguration: MapsWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);
        this.schedulerRunInterval = 300;
        this.map = null;
    }

    /**
     * Attempt to validate the configuration details.
     */
    protected validateConfigurationDetails(
        maybeRadarWidgetConfigurationDetails: MapsWidgetConfigurationDetails
    ): MapsWidgetConfigurationDetails {
        return ZodMapsWidgetConfigurationDetails.parse(maybeRadarWidgetConfigurationDetails);
    }

    /**
     * Initialize Map Widget setting hasContentToPlay after.
     */
    async initialize(): Promise<void> {
        await super.initialize();

        if (!this.config.key) {
            this.hasContentToPlay = false;
        } else {
            this.hasContentToPlay = true;
        }
    }

    /**
     * @TODO: If a failure of createMap should throw to the zone or swallow the error.
     * Verifies the config key is present and calls createMap.
     */
    async getReadyForDisplay(): Promise<void> {
        if (!this.config.key) {
            Log.Error(`Map API Key is missing.`, null, __fileline, null);
        } else {
            try {
                const mapIframe = this.createMap();
                this.wrapperElement.appendChild(mapIframe);
                /**
                 * When there are multiple layouts assigned at the same time,
                 *   when switching between them the first time both layout display properly.
                 * The second run the first layout, in my case the Maps widget, will not have
                 *   getReadyForDisplay get called, causing it to not be rendered on the page.
                 * The second run the other layout displays properly.
                 * After the second run it would work fine again.
                 * I'm not sure why, but adding the following code snippet "fixed" it though.
                 * await asyncTimeout(600);
                 */
            } catch (err) {
                Log.Error(`Google map failed to initialize.`, null, __fileline, err);
            }
        }
    }

    /**
     * Creates a new dom element which contains the iframe Google Map. Then
     * attempts to populate the Google Map data from their api.
     */
    createMap(): HTMLIFrameElement {
        const mapOptions: google.maps.MapOptions = {
            center: {
                lat: this.config.lat,
                lng: this.config.lng,
            },
            zoom: this.config.zoom,
            zoomControl: false,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            mapTypeId:
                this.config.mapType && this.config.mapType in MapTypeId
                    ? this.config.mapType.toLowerCase()
                    : null,
        };

        /**
         * Load the map in an iframe because we don't want signage to cache Google
         * images. Also for some reason it was really really slowing down the
         * signage.
         */
        const iframe: HTMLIFrameElement = document.createElement('iframe');
        iframe.style.border = 'none';
        iframe.id = `google_maps_${this.id}`;
        iframe.width = '100%';
        iframe.height = '100%';
        iframe.onload = async () => {
            await MapsWidget.loadGoogleMaps(this.config.key);
            Log.Info(`Google Maps Version: ${google.maps.version}`, null, __fileline, null);
            const mapWindow = iframe.contentWindow;

            if (!mapWindow) {
                throw new Error('No map element inside iframe.');
            }

            const map: google.maps.Map = new google.maps.Map(
                mapWindow.window.document.body,
                mapOptions
            );
            this.map = map;

            if (this.config.showTraffic) {
                const trafficLayer = new google.maps.TrafficLayer();
                trafficLayer.setMap(map);
            }
        };

        return iframe;
    }

    /**
     * Execute Google Maps Javascript library (if not already loaded)
     */
    static async loadGoogleMaps(apiKey: string): Promise<typeof google.maps | false> {
        // If the API has been created before.
        if (MapsWidget.googleMapsPromise) {
            return MapsWidget.googleMapsPromise;
        }

        // If google.maps exists then the API might be loaded already.
        if (window.google && google.maps) {
            MapsWidget.googleMapsPromise = Promise.resolve(google.maps);
            return MapsWidget.googleMapsPromise;
        }

        /**
         * We want to set googleMapsPromise once, so if this is called multiple times only one
         *   instance of mapsLoader is called. The inner then is to resolve google.maps whenever
         *   it is ready.
         */
        MapsWidget.googleMapsPromise = new Promise((resolve) => {
            const mapsLoader = new Loader({
                apiKey: apiKey,
            });

            mapsLoader.load().then(() => resolve(google.maps));
        });

        return MapsWidget.googleMapsPromise;
    }

    schedulerAction(): void {
        if (this.config.showTraffic) {
            this.reloadMapTiles();
        }
    }
    /**
     *  Triggers a refresh of the traffic layer of the map,
     *  by causing the map tiles to be reloaded.
     */
    reloadMapTiles(): void {
        if (this.map === null) {
            Log.Error('Cannot refresh map traffic data', null, __fileline);
            return;
        }
        this.map.setOptions({
            styles: [
                {
                    featureType: 'road',
                    elementType: 'geometry.fill',
                    stylers: [
                        {
                            saturation: Math.random(),
                        },
                    ],
                },
            ],
        });
    }
}
